import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { setUser } from '../../redux/userSlice';
import {
  enableTwoFactor,
  verifyTwoFactor,
  disableTwoFactor,
  getRecoveryCodes,
} from '../../api/api';
import CustomAlert from '../../components/CustomAlert';
import { useTranslation } from '../../hooks/useTranslation';

const TwoFactorSection = () => {
  const user = useSelector((state) => state.user.user);
  const dispatch = useDispatch();
  const t = useTranslation();

  const [loading, setLoading] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [showCodes, setShowCodes] = useState(null);

  const handleStartSetup = async () => {
    try {
      setLoading(true);
      const data = await enableTwoFactor();
      setSetupData(data);
    } catch (error) {
      CustomAlert.warning(
        t('warning'),
        error?.response?.data?.message || t('failed_to_start_2fa_setup')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerifySetup = async (e) => {
    e.preventDefault();
    if (!verificationCode) return;

    try {
      setLoading(true);
      await verifyTwoFactor({ code: verificationCode });

      // Update Redux state
      if (user) {
        dispatch(
          setUser({
            ...user,
            has_2fa_enabled: true,
          })
        );
      }

      CustomAlert.success(t('success'), t('2fa_enabled_success'));

      // Show recovery codes to save
      if (setupData?.recovery_codes) {
        setShowCodes(setupData.recovery_codes);
      }
      setSetupData(null);
      setVerificationCode('');
    } catch (error) {
      CustomAlert.warning(
        t('warning'),
        error?.response?.data?.errors?.code?.[0] ||
          error?.response?.data?.message ||
          t('verification_failed')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    CustomAlert.confirm(t('disable_2fa_title'), t('disable_2fa_confirm'), async () => {
      try {
        setLoading(true);
        await disableTwoFactor();

        if (user) {
          dispatch(
            setUser({
              ...user,
              has_2fa_enabled: false,
            })
          );
        }

        setShowCodes(null);

        CustomAlert.success(t('success'), t('2fa_disabled_success'));
      } catch (error) {
        CustomAlert.warning(
          t('warning'),
          error?.response?.data?.message || t('failed_to_disable_2fa')
        );
      } finally {
        setLoading(false);
      }
    });
  };

  const handleShowRecoveryCodes = async () => {
    if (showCodes) {
      setShowCodes(null);
      return;
    }

    try {
      setLoading(true);
      const data = await getRecoveryCodes();
      setShowCodes(data.recovery_codes);
    } catch (error) {
      CustomAlert.warning(
        t('warning'),
        error?.response?.data?.message || t('failed_to_retrieve_recovery_codes')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ marginTop: '24px', borderTop: '1px solid #ccc', paddingTop: '24px' }}>
      <h2>{t('two_factor_auth_title')}</h2>

      <p style={{ color: '#555', fontSize: '0.875rem', marginBottom: '16px', lineHeight: '1.4' }}>
        {user?.has_2fa_enabled ? t('2fa_active_desc') : t('2fa_inactive_desc')}
      </p>

      {/* STATUS & ACTIONS */}
      {!setupData && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {user?.has_2fa_enabled ? (
            <>
              <button
                type="button"
                className="button_account"
                style={{ background: '#ff364a', borderColor: '#ff364a' }}
                onClick={handleDisable}
                disabled={loading}
              >
                {t('disable_2fa_btn')}
              </button>
              <button
                type="button"
                className="button_account"
                onClick={handleShowRecoveryCodes}
                disabled={loading}
              >
                {showCodes ? t('hide_recovery_codes') : t('view_recovery_codes')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="button_account"
              onClick={handleStartSetup}
              disabled={loading}
            >
              {t('enable_2fa_btn')}
            </button>
          )}
        </div>
      )}

      {/* SETUP FLOW */}
      {setupData && (
        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            background: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e9ecef',
            color: '#333',
          }}
        >
          <h3 style={{ fontSize: '16px', fontWeight: 500, color: '#002060', marginBottom: '12px' }}>
            {t('setup_2fa_title')}
          </h3>

          <div
            style={{
              display: 'flex',
              gap: '20px',
              alignItems: 'flex-start',
              flexWrap: 'wrap',
              marginBottom: '20px',
            }}
          >
            <div
              style={{
                background: '#fff',
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
              }}
              dangerouslySetInnerHTML={{ __html: setupData.qr_code_svg }}
            />
            <div style={{ flex: 1, minWidth: '200px' }}>
              <p style={{ fontSize: '0.875rem', marginBottom: '8px', color: '#333' }}>
                {t('setup_2fa_step1')}
              </p>
              <p style={{ fontSize: '0.875rem', marginBottom: '12px', color: '#333' }}>
                {t('setup_2fa_step2')}
              </p>
              <code
                style={{
                  display: 'block',
                  padding: '8px',
                  background: '#e9ecef',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: 'bold',
                  letterSpacing: '1px',
                  marginBottom: '12px',
                  textAlign: 'center',
                }}
              >
                {setupData.secret}
              </code>
            </div>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <p
              style={{
                fontSize: '0.875rem',
                fontWeight: 600,
                color: '#ff364a',
                marginBottom: '8px',
              }}
            >
              {t('save_recovery_codes_warning')}
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '8px',
                padding: '12px',
                background: '#fff',
                borderRadius: '4px',
                border: '1px solid #dee2e6',
              }}
            >
              {setupData.recovery_codes.map((code) => (
                <code key={code} style={{ fontSize: '0.8125rem', color: '#333' }}>
                  {code}
                </code>
              ))}
            </div>
          </div>

          <form
            onSubmit={handleVerifySetup}
            style={{ borderTop: '1px solid #dee2e6', paddingTop: '16px' }}
          >
            <p style={{ fontSize: '0.875rem', marginBottom: '8px', color: '#333' }}>
              {t('enter_authenticator_code_label')}
            </p>
            <input
              type="text"
              placeholder={t('six_digit_code_placeholder')}
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              required
              className="login-input"
              style={{ marginBottom: '12px', width: '100%' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button type="submit" className="button_account" disabled={loading}>
                {t('verify_and_enable')}
              </button>
              <button
                type="button"
                className="button_account"
                style={{ background: '#6c757d', borderColor: '#6c757d' }}
                onClick={() => setSetupData(null)}
              >
                {t('Cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* RECOVERY CODES DISPLAY */}
      {showCodes && (
        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            background: '#fdf7f7',
            borderRadius: '8px',
            border: '1px solid #f5c6cb',
            color: '#721c24',
          }}
        >
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#721c24', marginBottom: '8px' }}>
            {t('recovery_codes_title')}
          </h3>
          <p style={{ fontSize: '0.8125rem', color: '#721c24', marginBottom: '12px' }}>
            {t('recovery_codes_desc')}
          </p>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '8px',
              padding: '12px',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #dee2e6',
              marginBottom: '12px',
            }}
          >
            {showCodes.map((code) => (
              <code key={code} style={{ fontSize: '0.8125rem', color: '#333' }}>
                {code}
              </code>
            ))}
          </div>
          <button type="button" className="button_account" onClick={() => setShowCodes(null)}>
            {t('CLOSE')}
          </button>
        </div>
      )}
    </div>
  );
};

export default TwoFactorSection;
